import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Eye, Trash, FileText, ArrowsLeftRight, X, WarningCircle, PencilSimple, NotePencil } from "@phosphor-icons/react";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso.slice(0, 10); }
}

export default function History() {
  const [reports, setReports] = useState([]);
  const [compareMode, setCompareMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "drafts" ? "drafts" : "reports";
  const navigate = useNavigate();

  const load = () => api.get("/reports").then((r) => setReports(r.data));
  useEffect(() => { load(); }, []);

  const setTab = (t) => setSearchParams(t === "drafts" ? { tab: "drafts" } : {});

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this report?")) return;
    await api.delete(`/reports/${id}`); toast.success("Report deleted"); load();
  };

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelected((p) => {
      if (p.includes(id)) return p.filter((x) => x !== id);
      if (p.length >= 2) return [p[1], id];
      return [...p, id];
    });
  };

  const runCompare = () => {
    if (selected.length !== 2) return toast.error("Select exactly 2 reports");
    navigate(`/compare?a=${selected[0]}&b=${selected[1]}`);
  };

  const statusCls = (s) => s === "generating" ? "bg-amber-50 text-amber-700 border-amber-200"
    : s === "failed" ? "bg-red-50 text-red-700 border-red-200"
    : s === "draft" ? "bg-slate-100 text-slate-600 border-slate-200"
    : "bg-emerald-50 text-emerald-700 border-emerald-200";

  const drafts = reports.filter((r) => r.status === "draft");
  const nonDrafts = reports.filter((r) => r.status !== "draft");
  const rows = tab === "drafts" ? drafts : nonDrafts;
  const openRow = (r) => tab === "drafts" ? navigate(`/generate?draft=${r.id}`) : navigate(`/reports/${r.id}`);

  return (
    <div className="space-y-8" data-testid="history-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-semibold text-[#b45309] bg-[#fef3e2] rounded-full px-3 py-1 inline-block">Archive</div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mt-3">Reports History</h1>
          <p className="text-slate-500 text-base mt-1">All generated CMD dashboard reports and saved drafts.</p>
        </div>
        {tab === "reports" && (
          <div className="flex items-center gap-2">
            {compareMode ? (
              <>
                <span className="text-xs text-slate-500">{selected.length}/2 selected</span>
                <Button data-testid="run-compare-btn" onClick={runCompare} disabled={selected.length !== 2} className="gold-btn rounded-xl h-11 px-4 gap-2"><ArrowsLeftRight size={16} weight="bold" /> Compare</Button>
                <Button data-testid="exit-compare-btn" variant="ghost" onClick={() => { setCompareMode(false); setSelected([]); }} className="text-slate-500 h-11 gap-1"><X size={16} /> Cancel</Button>
              </>
            ) : (
              <Button data-testid="compare-mode-btn" onClick={() => setCompareMode(true)} className="bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-xl h-11 px-4 gap-2"><ArrowsLeftRight size={16} /> Compare Reports</Button>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="inline-flex rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="history-tabs">
        <button data-testid="tab-reports" onClick={() => setTab("reports")}
          className={`text-sm px-5 py-2.5 font-medium transition-colors ${tab === "reports" ? "bg-[#004182] text-white" : "text-slate-600 hover:bg-slate-50"}`}>
          Reports ({nonDrafts.length})
        </button>
        <button data-testid="tab-drafts" onClick={() => setTab("drafts")}
          className={`text-sm px-5 py-2.5 font-medium transition-colors flex items-center gap-1.5 ${tab === "drafts" ? "bg-[#004182] text-white" : "text-slate-600 hover:bg-slate-50"}`}>
          <NotePencil size={16} /> Drafts ({drafts.length})
        </button>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr className="text-left">
              {compareMode && tab === "reports" && <th className="px-5 py-3 w-10"></th>}
              <th className="px-5 py-3 font-medium">Report</th>
              <th className="px-5 py-3 font-medium hidden sm:table-cell">Issue</th>
              <th className="px-5 py-3 font-medium hidden md:table-cell">Issue Date</th>
              {tab === "reports" && <th className="px-5 py-3 font-medium hidden md:table-cell">Generated</th>}
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3 font-medium hidden lg:table-cell">By</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`report-row-${r.id}`}
                onClick={() => (compareMode && tab === "reports") ? null : openRow(r)}
                className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer">
                {compareMode && tab === "reports" && (
                  <td className="px-5 py-3" onClick={(e) => toggleSelect(r.id, e)}>
                    <Checkbox checked={selected.includes(r.id)} data-testid={`compare-check-${r.id}`} />
                  </td>
                )}
                <td className="px-5 py-3 text-slate-900 font-medium flex items-center gap-2 max-w-[380px]">
                  <FileText size={18} className="text-[#004182] shrink-0" />
                  <span className="truncate">{r.title}</span>
                  {r.failed_sections > 0 && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-red-600 bg-red-50 border border-red-200 rounded-full px-1.5 py-0.5 shrink-0" title={`${r.failed_sections} section(s) with source issues`}>
                      <WarningCircle size={11} weight="fill" /> {r.failed_sections}
                    </span>
                  )}
                </td>
                <td className="px-5 py-3 text-slate-500 hidden sm:table-cell">{r.issue_no || "—"}</td>
                <td className="px-5 py-3 text-slate-500 hidden md:table-cell">{r.issue_date || "—"}</td>
                {tab === "reports" && <td className="px-5 py-3 text-slate-500 hidden md:table-cell" data-testid={`report-generated-${r.id}`}>{fmtDate(r.generated_at)}</td>}
                <td className="px-5 py-3"><span className={`text-[11px] px-2.5 py-1 rounded-full capitalize border font-medium ${statusCls(r.status)}`}>{r.status}</span></td>
                <td className="px-5 py-3 text-slate-500 hidden lg:table-cell">{r.created_by}</td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  {tab === "drafts" ? (
                    <button data-testid={`edit-draft-${r.id}`} onClick={(e) => { e.stopPropagation(); navigate(`/generate?draft=${r.id}`); }} className="text-slate-400 hover:text-[#004182] mr-3" title="Continue draft"><PencilSimple size={17} /></button>
                  ) : (
                    <button data-testid={`view-report-${r.id}`} onClick={(e) => { e.stopPropagation(); navigate(`/reports/${r.id}`); }} className="text-slate-400 hover:text-[#004182] mr-3" title="View"><Eye size={17} /></button>
                  )}
                  <button data-testid={`delete-report-${r.id}`} onClick={(e) => remove(r.id, e)} className="text-slate-400 hover:text-red-500" title="Delete"><Trash size={16} /></button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={10} className="px-5 py-10 text-center text-slate-400">{tab === "drafts" ? "No saved drafts yet. Save a draft from the Generate Report wizard." : "No reports generated yet."}</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
